import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";

// Same text the app shows: edited by the admin in « All Pages ».
const API = "https://omni-cave.com/backend/api/v1/auth/get-termCondition";

export const LEGAL_PAGES = {
  privacy: {
    path: "/politique-de-confidentialite",
    type: "Privacy",
    title: "Politique de confidentialité",
  },
  terms: {
    path: "/conditions-generales",
    type: "Terms",
    title: "Conditions générales d'utilisation",
  },
};

const DROP_TAGS = "script,style,iframe,object,embed,form,input,button,textarea,select,link,meta,base";
const KEEP_STYLE = /^(text-align|font-weight|font-style|text-decoration)$/i;

/**
 * The page text comes from the admin's rich-text editor. Keep the structure
 * (headings, lists, links, bold…) but remove anything that could run code,
 * and the editor's colours/fonts so the page uses the site's typography.
 */
const sanitize = (html) => {
  const doc = new DOMParser().parseFromString(String(html || ""), "text/html");
  doc.querySelectorAll(DROP_TAGS).forEach((el) => el.remove());
  doc.body.querySelectorAll("*").forEach((el) => {
    [...el.attributes].forEach((attr) => {
      const name = attr.name.toLowerCase();
      if (name.startsWith("on")) el.removeAttribute(attr.name);
      else if ((name === "href" || name === "src") && /^\s*(javascript|data|vbscript):/i.test(attr.value)) {
        el.removeAttribute(attr.name);
      } else if (name === "style") {
        const kept = attr.value
          .split(";")
          .map((d) => d.trim())
          .filter((d) => KEEP_STYLE.test(d.split(":")[0].trim()))
          .join("; ");
        if (kept) el.setAttribute("style", kept);
        else el.removeAttribute("style");
      } else if (!["href", "src", "alt", "title", "colspan", "rowspan"].includes(name)) {
        el.removeAttribute(attr.name);
      }
    });
    if (el.tagName === "A") {
      el.setAttribute("target", "_blank");
      el.setAttribute("rel", "noopener noreferrer");
    }
  });
  return doc.body.innerHTML;
};

/** « Politique de confidentialité » / « Conditions générales d'utilisation ». */
export default function LegalPage({ page }) {
  const { type, title } = LEGAL_PAGES[page];
  const [state, setState] = useState({ status: "loading", html: "" });

  const load = useCallback(() => {
    setState({ status: "loading", html: "" });
    fetch(API, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ type }),
    })
      .then((r) => r.json())
      .then((res) => {
        const html = res?.success ? sanitize(res.body?.description) : "";
        setState(html.trim() ? { status: "ready", html } : { status: "error", html: "" });
      })
      .catch(() => setState({ status: "error", html: "" }));
  }, [type]);

  useEffect(() => {
    document.title = `${title} | OmniCave`;
    load();
    return () => {
      document.title = "OmniCave";
    };
  }, [load, title]);

  return (
    <section className="legalPage">
      <div className="container">
        <nav className="legalPage__crumbs">
          <Link to="/">Accueil</Link>
          <span>/</span>
          <span>{title}</span>
        </nav>
        <h1>{title}</h1>

        {state.status === "loading" && (
          <div className="legalPage__state">
            <span className="legalPage__spinner" aria-hidden="true" />
            Chargement…
          </div>
        )}
        {state.status === "error" && (
          <div className="legalPage__state">
            <p>Cette page n'a pas pu être chargée.</p>
            <button type="button" className="legalPage__retry" onClick={load}>
              Réessayer
            </button>
          </div>
        )}
        {state.status === "ready" && (
          <div className="legalPage__body" dangerouslySetInnerHTML={{ __html: state.html }} />
        )}
      </div>
    </section>
  );
}
